'use client';

import { type ReactNode, createContext, useContext } from 'react';

import {
  DollarSign,
  Euro,
  IndianRupee,
  JapaneseYen,
  type LucideIcon,
  PoundSterling,
  RussianRuble,
  SaudiRiyal,
  SwissFranc,
} from 'lucide-react';

import { cn } from '../lib/utils';

/**
 * React context that carries the active tenant currency for any descendant
 * `<CurrencyIcon />` (and consumers calling `useTenantCurrency()`) so they
 * don't have to thread a `code` prop through dozens of component layers.
 *
 * The provider should be mounted at the highest tenant-aware layout (e.g.
 * `(tenant)/layout.tsx` and `/home/[account]/layout.tsx`) with the value
 * loaded from `business_settings.currency`. When no provider is mounted
 * the default is `SAR`, which preserves existing behavior on routes that
 * predate the switch.
 */
const TenantCurrencyContext = createContext<string>('SAR');

export function TenantCurrencyProvider({
  value,
  children,
}: {
  value: string;
  children: ReactNode;
}) {
  return (
    <TenantCurrencyContext.Provider value={value || 'SAR'}>
      {children}
    </TenantCurrencyContext.Provider>
  );
}

/** Read the active tenant currency from the nearest provider. */
export function useTenantCurrency(): string {
  return useContext(TenantCurrencyContext);
}

/**
 * ISO 4217 codes that ship with a dedicated Lucide icon. Codes outside
 * this map render their three-letter ISO code as text (e.g. JOD, AED, KWD)
 * since no symbol exists — the abbreviation is more informative than a
 * generic banknote glyph.
 *
 * Single source of truth for currency-glyph rendering. Replaces the
 * direct `import { SaudiRiyal } from 'lucide-react'` pattern that was
 * scattered across ~85 files.
 */
const CURRENCY_ICON_MAP: Record<string, LucideIcon> = {
  SAR: SaudiRiyal,
  USD: DollarSign,
  EUR: Euro,
  GBP: PoundSterling,
  JPY: JapaneseYen,
  CNY: JapaneseYen, // CNY shares the yen/yuan glyph
  INR: IndianRupee,
  RUB: RussianRuble,
  CHF: SwissFranc,
};

interface CurrencyIconProps extends React.SVGProps<SVGSVGElement> {
  /**
   * ISO 4217 alpha-3 currency code (e.g. 'SAR', 'AED', 'USD'). Optional —
   * when omitted the icon reads from `TenantCurrencyContext`. Pass
   * explicitly only when rendering an amount in a *different* currency
   * than the active tenant (e.g. a refund showing the original currency).
   */
  code?: string;
  /**
   * Optional Lucide icon to use when `code` has no dedicated glyph in
   * `CURRENCY_ICON_MAP`. When omitted (the default), the ISO code is
   * rendered as text instead.
   */
  fallback?: LucideIcon;
  className?: string;
}

/**
 * Renders the appropriate Lucide currency glyph for an ISO 4217 code,
 * or the bare three-letter code as text when no symbol exists.
 *
 *   <CurrencyIcon />                // tenant currency from context
 *   <CurrencyIcon code="USD" />     // $ glyph
 *   <CurrencyIcon code="JOD" />     // "JOD" as text (no Lucide glyph)
 *
 * Use without `code` for "the price the customer is being shown" rendering;
 * use with `code` only when the amount is denominated in a known different
 * currency (e.g. historical record).
 */
export function CurrencyIcon({
  code,
  fallback,
  className,
  ...props
}: CurrencyIconProps) {
  // Hooks must run unconditionally; consume context every render.
  const tenantCurrency = useContext(TenantCurrencyContext);
  const resolved = (code ?? tenantCurrency ?? 'SAR').toUpperCase();
  const Icon = CURRENCY_ICON_MAP[resolved] ?? fallback;

  if (Icon) {
    return (
      <Icon
        aria-label={resolved || 'Currency'}
        className={cn(className)}
        {...props}
      />
    );
  }

  // No Lucide glyph for this code (e.g. JOD, AED, KWD, BHD) — fall back to
  // the bare ISO abbreviation. Inherits font size/color from the parent so
  // it lines up with the adjacent amount. Trailing `h-auto w-auto` cancels
  // any `h-4 w-4`-style icon-sizing classes callers pass; tailwind-merge
  // resolves the conflict in favor of the later value.
  return (
    <span
      aria-label={resolved || 'Currency'}
      className={cn(
        'inline-flex items-center leading-none font-medium',
        className,
        'h-auto w-auto',
      )}
    >
      {resolved}
    </span>
  );
}

/**
 * Right-aligned header for a money column (label only — the currency symbol
 * lives on each row via <AmountCell>). The house pattern for every table's
 * amount column.
 */
export function AmountHeader({ children }: { children: ReactNode }) {
  return <span className="block text-right whitespace-nowrap">{children}</span>;
}

/**
 * Right-aligned money value: the tenant CurrencyIcon next to the amount, with
 * tabular figures so decimal points line up down the column.
 */
export function AmountCell({
  value,
  code,
  className,
}: {
  value: number;
  code?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-1 font-medium tabular-nums',
        className,
      )}
    >
      <CurrencyIcon code={code} className="h-3.5 w-3.5 shrink-0" />
      <span>{value.toFixed(2)}</span>
    </div>
  );
}
